import { useCallback, useEffect, useMemo, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { MdmShell, Field, StatusBadge, ErrorAlert, s } from './_mdmKit.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

// M05 §7/§8 (T061) — Painter Master. Full CRUD over the existing backend
// `@Controller('v1/master/painters')`:
//   GET  /master/painters?vendorId&status&territoryCode&skillType&page&limit → { data, total }
//   POST /master/painters (CreatePainterDto) · PUT /master/painters/:id
//   PATCH /master/painters/:id/deactivate
// Painters belong to a vendor, so the vendor picker is sourced live from
// /master/painters/vendors (a vendor login gets only itself). Client
// validation mirrors the backend mobile regex.

interface Painter {
  readonly painterId: string;
  readonly painterCode: string;
  readonly vendorId: string;
  readonly painterName: string;
  readonly mobileNumber: string;
  readonly skillTypes: string[];
  readonly experienceYears: number;
  /** Null: the tenant's figure on System Config applies. */
  readonly maxConcurrentJobs: number | null;
  readonly recordStatusCode: string;
}
interface VendorOpt { readonly vendorId: string; readonly vendorName: string; readonly vendorCode: string; }

const STATUSES = ['ACTIVE', 'INACTIVE', 'ARCHIVED'] as const;
const MOBILE_RE = /^[6-9][0-9]{9}$/;

const EMPTY = {
  painterCode: '', vendorId: '', painterName: '', mobileNumber: '', address: '',
  skillTypes: '', territoryCoverage: '', experienceYears: '', maxConcurrentJobs: '',
};
type Form = typeof EMPTY;

const PAGE_SIZE = 20;
const csv = (x: string): string[] | undefined => {
  const arr = x.split(',').map((v) => v.trim()).filter(Boolean);
  return arr.length ? arr : undefined;
};

export function PainterMasterPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<Painter[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [skillFilter, setSkillFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [vendors, setVendors] = useState<VendorOpt[]>([]);

  const [form, setForm] = useState<Form>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (vendorFilter) params.set('vendorId', vendorFilter);
      if (statusFilter) params.set('status', statusFilter);
      if (skillFilter.trim()) params.set('skillType', skillFilter.trim());
      const r = await apiRequest<{ data: Painter[]; total: number }>(
        `/master/painters?${params.toString()}`, { token },
      );
      setRows(r.data ?? []);
      setTotal(r.total ?? 0);
      setError(null);
    } catch (e) { setError((e as Error).message); setRows([]); }
  }, [token, page, vendorFilter, statusFilter, skillFilter]);

  const loadVendors = useCallback(async () => {
    try {
      const r = await apiRequest<{ data: VendorOpt[]; total: number }>(
        '/master/painters/vendors?limit=200', { token },
      );
      setVendors(r.data ?? []);
    } catch { setVendors([]); }
  }, [token]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { void loadVendors(); }, [loadVendors]);

  const vendorName = useCallback(
    (id: string) => vendors.find((v) => v.vendorId === id)?.vendorName ?? '—',
    [vendors],
  );

  const set = (k: keyof Form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const beginCreate = () => {
    // A vendor user is served exactly one vendor, so there is nothing to pick.
    const only = vendors.length === 1 ? vendors[0].vendorId : '';
    setForm({ ...EMPTY, vendorId: only });
    setEditingId(null); setFormError(null); setShowForm(true);
  };
  const beginEdit = useCallback(async (p: Painter) => {
    setForm({
      ...EMPTY,
      painterCode: p.painterCode, vendorId: p.vendorId, painterName: p.painterName,
      mobileNumber: p.mobileNumber, skillTypes: (p.skillTypes ?? []).join(', '),
      experienceYears: p.experienceYears == null ? '' : String(p.experienceYears),
      maxConcurrentJobs: p.maxConcurrentJobs == null ? '' : String(p.maxConcurrentJobs),
    });
    setEditingId(p.painterId); setFormError(null); setShowForm(true);
    try {
      const full = await apiRequest<Record<string, unknown>>(
        `/master/painters/${encodeURIComponent(p.painterId)}`, { token },
      );
      const arr = (k: string) => Array.isArray(full[k]) ? (full[k] as string[]).join(', ') : '';
      const str = (k: string) => (full[k] == null ? '' : String(full[k]));
      setForm((f) => ({
        ...f,
        vendorId: str('vendorId') || f.vendorId,
        painterName: str('painterName') || f.painterName,
        mobileNumber: str('mobileNumber') || f.mobileNumber,
        address: str('address'),
        skillTypes: arr('skillTypes'), territoryCoverage: arr('territoryCoverage'),
        experienceYears: full.experienceYears == null ? '' : String(full.experienceYears),
        maxConcurrentJobs: full.maxConcurrentJobs == null ? '' : String(full.maxConcurrentJobs),
      }));
    } catch { /* keep summary values if the detail fetch fails */ }
  }, [token]);

  const clientValidate = (): string | null => {
    if (!MOBILE_RE.test(form.mobileNumber)) return t('mdm.common.invalidMobile');
    if (!form.vendorId) return t('mdm.painter.vendorRequired');
    if (form.experienceYears) {
      const n = Number(form.experienceYears);
      if (Number.isNaN(n) || n < 0) return t('mdm.painter.invalidExp');
    }
    if (form.maxConcurrentJobs.trim()) {
      const n = Number(form.maxConcurrentJobs);
      if (!Number.isInteger(n) || n < 1) return t('mdm.painter.invalidMaxJobs');
    }
    return null;
  };

  const onSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    const v = clientValidate();
    if (v) { setFormError(v); return; }
    setSaving(true); setFormError(null);
    const trimmed = (x: string) => (x.trim() ? x.trim() : undefined);
    const exp = form.experienceYears.trim() ? Number(form.experienceYears) : undefined;
    // Empty clears the painter's own limit, so the tenant figure applies again.
    const maxJobs = form.maxConcurrentJobs.trim() ? Number(form.maxConcurrentJobs) : null;
    try {
      if (editingId) {
        await apiRequest(`/master/painters/${encodeURIComponent(editingId)}`, {
          method: 'PUT', token,
          body: {
            vendorId: form.vendorId, painterName: form.painterName.trim(),
            mobileNumber: form.mobileNumber.trim(), address: trimmed(form.address),
            skillTypes: csv(form.skillTypes), territoryCoverage: csv(form.territoryCoverage),
            experienceYears: exp, maxConcurrentJobs: maxJobs,
          },
        });
      } else {
        await apiRequest('/master/painters', {
          method: 'POST', token,
          body: {
            vendorId: form.vendorId,
            painterName: form.painterName.trim(), mobileNumber: form.mobileNumber.trim(),
            address: trimmed(form.address), skillTypes: csv(form.skillTypes),
            territoryCoverage: csv(form.territoryCoverage), experienceYears: exp,
            maxConcurrentJobs: maxJobs,
          },
        });
      }
      setShowForm(false); setForm(EMPTY); setEditingId(null);
      await load();
    } catch (err) { setFormError((err as Error).message); }
    finally { setSaving(false); }
  }, [editingId, form, token, load]);

  const onDeactivate = useCallback(async (p: Painter) => {
    setError(null);
    try {
      await apiRequest(`/master/painters/${encodeURIComponent(p.painterId)}/deactivate`, { method: 'PATCH', token });
      await load();
    } catch (e) { setError((e as Error).message); }
  }, [token, load]);

  const pages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  return (
    <MdmShell
      label="painter-master"
      title={t('mdm.painter.title')}
      description={t('mdm.painter.desc')}
      actions={<Button type="button" variant="primary" data-testid="new-painter" onClick={beginCreate}>{t('mdm.painter.newBtn')}</Button>}
    >
      <form aria-label="painter-filters" onSubmit={(e) => { e.preventDefault(); setPage(1); void load(); }}
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        {vendors.length > 1 && (
          <Field id="pnt-vendor" label={t('mdm.painter.vendorLabel')}>
            <select id="pnt-vendor" value={vendorFilter} onChange={(e) => { setVendorFilter(e.target.value); setPage(1); }} style={s.input}>
              <option value="">{t('mdm.painter.allVendors')}</option>
              {vendors.map((v) => <option key={v.vendorId} value={v.vendorId}>{v.vendorName}</option>)}
            </select>
          </Field>
        )}
        <Field id="pnt-status" label={t('mdm.col.status')}>
          <select id="pnt-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} style={s.input}>
            <option value="">{t('mdm.common.all')}</option>
            {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </Field>
        <Field id="pnt-skill" label={t('mdm.painter.skillLabel')}>
          <input id="pnt-skill" value={skillFilter} onChange={(e) => setSkillFilter(e.target.value)} style={s.input} placeholder={t('mdm.painter.skillPh')} />
        </Field>
        <Button type="submit" variant="secondary" data-testid="apply-filters">{t('mdm.common.search')}</Button>
      </form>

      {showForm && (
        <form onSubmit={onSubmit} aria-label={editingId ? 'edit-painter-form' : 'create-painter-form'} style={s.card}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>{editingId ? t('mdm.painter.editTitle') : t('mdm.painter.newTitle')}</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {editingId && (
              <Field id="p-painterCode" label={t('mdm.painter.painterCode')}>
                <input id="p-painterCode" value={form.painterCode} readOnly disabled style={s.input} />
              </Field>
            )}
            <Field id="p-vendorId" label={t('mdm.painter.vendorReq')}>
              <select id="p-vendorId" value={form.vendorId} onChange={set('vendorId')} required disabled={vendors.length === 1} style={s.input}>
                {vendors.length > 1 && <option value="">{t('mdm.painter.selectVendor')}</option>}
                {vendors.map((v) => <option key={v.vendorId} value={v.vendorId}>{v.vendorName} ({v.vendorCode})</option>)}
              </select>
            </Field>
            <Field id="p-painterName" label={t('mdm.painter.painterName')}>
              <input id="p-painterName" value={form.painterName} onChange={set('painterName')} required style={s.input} />
            </Field>
            <Field id="p-mobileNumber" label={t('mdm.painter.mobile')} hint={t('mdm.common.mobileHint')}>
              <input id="p-mobileNumber" value={form.mobileNumber} onChange={set('mobileNumber')} required style={s.input} />
            </Field>
            <Field id="p-address" label={t('mdm.painter.address')}>
              <input id="p-address" value={form.address} onChange={set('address')} style={s.input} />
            </Field>
            <Field id="p-skillTypes" label={t('mdm.painter.skillTypes')} hint={t('mdm.painter.csvHint')}>
              <input id="p-skillTypes" value={form.skillTypes} onChange={set('skillTypes')} style={s.input} />
            </Field>
            <Field id="p-territoryCoverage" label={t('mdm.painter.territoryCoverage')} hint={t('mdm.painter.csvHint')}>
              <input id="p-territoryCoverage" value={form.territoryCoverage} onChange={set('territoryCoverage')} style={s.input} />
            </Field>
            <Field id="p-experienceYears" label={t('mdm.painter.experience')} hint={t('mdm.painter.experienceHint')}>
              <input id="p-experienceYears" type="number" min={0} value={form.experienceYears} onChange={set('experienceYears')} style={s.input} />
            </Field>
            <Field id="p-maxConcurrentJobs" label={t('mdm.painter.maxJobs')} hint={t('mdm.painter.maxJobsHint')}>
              <input id="p-maxConcurrentJobs" type="number" min={1} step={1} value={form.maxConcurrentJobs} onChange={set('maxConcurrentJobs')} style={s.input} />
            </Field>
          </div>
          <ErrorAlert message={formError} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button type="submit" variant="primary" data-testid="save-painter" disabled={saving}>
              {saving ? t('mdm.common.saving') : editingId ? t('mdm.common.saveChanges') : t('mdm.painter.createBtn')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>{t('mdm.common.cancel')}</Button>
          </div>
        </form>
      )}

      <ErrorAlert message={error} />
      {!rows ? (
        <p>{t('mdm.common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="painters-empty" style={{ color: s.muted }}>{t('mdm.painter.empty')}</p>
      ) : (
        <>
          <DataTable<Painter>
            ariaLabel="painters-table"
            testId="painters-table"
            rows={rows}
            rowKey={(p) => p.painterId}
            rowTestId={(p) => `painter-row-${p.painterId}`}
            columns={[
              { key: 'painterCode', header: t('mdm.painter.colPainterCode'), render: (p) => p.painterCode },
              { key: 'painterName', header: t('mdm.painter.colName'), render: (p) => p.painterName },
              { key: 'vendor', header: t('mdm.painter.colVendor'), render: (p) => vendorName(p.vendorId) },
              { key: 'mobileNumber', header: t('mdm.painter.colMobile'), render: (p) => p.mobileNumber },
              { key: 'skills', header: t('mdm.painter.colSkills'), render: (p) => (p.skillTypes ?? []).join(', ') || '—' },
              { key: 'exp', header: t('mdm.painter.colExp'), render: (p) => p.experienceYears ?? 0 },
              { key: 'maxJobs', header: t('mdm.painter.colMaxJobs'), render: (p) => p.maxConcurrentJobs ?? '—' },
              { key: 'status', header: t('mdm.col.status'), render: (p) => <StatusBadge status={p.recordStatusCode} /> },
              {
                key: 'actions',
                header: t('mdm.col.actions'),
                render: (p) => (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button type="button" variant="secondary" data-testid={`edit-${p.painterId}`} onClick={() => void beginEdit(p)}>{t('mdm.common.edit')}</Button>
                    {p.recordStatusCode === 'ACTIVE' && (
                      <Button type="button" variant="danger" data-testid={`deactivate-${p.painterId}`} onClick={() => onDeactivate(p)}>{t('mdm.common.deactivate')}</Button>
                    )}
                  </div>
                ),
              },
            ]}
          />
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
            <Button type="button" variant="secondary" disabled={page <= 1} data-testid="prev-page" onClick={() => setPage((p) => Math.max(1, p - 1))}>{t('mdm.common.prev')}</Button>
            <span style={{ fontSize: 13, color: s.muted }} data-testid="page-info">{t('mdm.painter.pageInfo', { page, pages, total })}</span>
            <Button type="button" variant="secondary" disabled={page >= pages} data-testid="next-page" onClick={() => setPage((p) => Math.min(pages, p + 1))}>{t('mdm.common.next')}</Button>
          </div>
        </>
      )}
    </MdmShell>
  );
}
